import { z } from 'zod';
import {
    Bucket,
    QuestionType,
    BooleanDisplayFormat,
    AudioType,
    QuizUserStatus,
} from '../../entities/quiz/enums';
import { StudentGender } from '../../entities/user/enums';
import { ArticleSummarySchema } from '../article';

// Answer Props Schemas
const BooleanAnswerPropsSchema = z.object({
    displayFormat: z.nativeEnum(BooleanDisplayFormat),
});

const NumericAnswerPropsSchema = z.object({
    min: z.number(),
    max: z.number(),
    useStrictMax: z.boolean().optional(),
    units: z.string().optional(),
});

const PercentageAnswerPropsSchema = z.object({});

const SingleChoiceAnswerPropsSchema = z.object({
    options: z.array(z.string()),
});

const AnswerPropsSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(QuestionType.Boolean),
        props: BooleanAnswerPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Numeric),
        props: NumericAnswerPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Percentage),
        props: PercentageAnswerPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.SingleChoice),
        props: SingleChoiceAnswerPropsSchema,
    }),
]);

// Actual Values Schemas
const GenderBreakdownSchema = <T extends z.ZodTypeAny>(valueSchema: T) =>
    z
        .object({
            [StudentGender.daughter]: valueSchema,
            [StudentGender.son]: valueSchema,
            [StudentGender.preferNotToAnswer]: valueSchema,
        })
        .partial();

const NumericActualValuesSchema = GenderBreakdownSchema(z.number());
const PercentageActualValuesSchema = GenderBreakdownSchema(z.number());
const BooleanActualValuesSchema = GenderBreakdownSchema(z.boolean());
const SingleChoiceActualValuesSchema = GenderBreakdownSchema(
    z.record(z.string(), z.number()),
);

const ActualValuesSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(QuestionType.Boolean),
        values: BooleanActualValuesSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Numeric),
        values: NumericActualValuesSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Percentage),
        values: PercentageActualValuesSchema,
    }),
    z.object({
        type: z.literal(QuestionType.SingleChoice),
        values: SingleChoiceActualValuesSchema,
    }),
]);

// Results Props Schemas
const BooleanResultsPropsSchema = z.object({
    trueValue: z.nativeEnum(Bucket),
    falseValue: z.nativeEnum(Bucket),
});

const NumericResultsPropsSchema = z.object({
    threshold: z.number(),
    belowThreshold: z.nativeEnum(Bucket),
    aboveOrEqualThreshold: z.nativeEnum(Bucket),
});

const PercentageResultsPropsSchema = z.object({
    threshold: z.number(),
    belowThreshold: z.nativeEnum(Bucket),
    aboveOrEqualThreshold: z.nativeEnum(Bucket),
});

const SingleChoiceResultsPropsSchema = z.object({
    optionToBucketMap: z.record(z.string(), z.nativeEnum(Bucket)),
});

const ResultsPropsSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(QuestionType.Boolean),
        props: BooleanResultsPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Numeric),
        props: NumericResultsPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.Percentage),
        props: PercentageResultsPropsSchema,
    }),
    z.object({
        type: z.literal(QuestionType.SingleChoice),
        props: SingleChoiceResultsPropsSchema,
    }),
]);

// Image URLs Schema
const ImageUrlsSchema = z
    .object({
        original: z.string().url(),
        small: z.string().url(),
        medium: z.string().url(),
        large: z.string().url(),
    })
    .optional();

// Video Info Schema
const VideoInfoSchema = z.object({
    fileName: z.string(),
    url: z.string().url(),
});

// Audio Info Schema (same structure as video)
const AudioInfoSchema = z.object({
    fileName: z.string(),
    url: z.string().url(),
    transcript: z.string().optional(),
});

// Question Schema
const QuestionSchema = z.object({
    id: z.string(),
    number: z.number(),
    question: z.string(),
    type: z.nativeEnum(QuestionType),
    image: ImageUrlsSchema,
    answerProps: AnswerPropsSchema,
    actualValues: ActualValuesSchema.optional(),
    audios: z
        .object({
            [AudioType.Correct]: AudioInfoSchema.optional(),
            [AudioType.Incorrect]: AudioInfoSchema.optional(),
            [AudioType.Underestimated]: AudioInfoSchema.optional(),
            [AudioType.Overestimated]: AudioInfoSchema.optional(),
        })
        .optional(),
    isBucketQuestion: z.boolean(),
    resultsProps: ResultsPropsSchema.optional(),
    pairWithResultNumber: z.number().int().positive().optional(),
    hideFromResults: z.boolean().optional(),
});

// Quiz Schema
export const QuizSchema = z.object({
    _id: z.string(),
    number: z.number(),
    title: z.string().optional(),
    titleImage: ImageUrlsSchema,
    videos: z
        .object({
            [Bucket.One]: VideoInfoSchema.optional(),
            [Bucket.Two]: VideoInfoSchema.optional(),
        })
        .optional(),
    isDraft: z.boolean(),
    questions: z.array(QuestionSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});

// Response Schemas
export const QuizResponseSchema = z.object({
    quiz: QuizSchema,
});

export const QuizzesListResponseSchema = z.object({
    quizzes: z.array(QuizSchema),
});

// Quiz List Item Schema (for user quiz list)
export const QuizListItemSchema = z.object({
    _id: z.string(),
    number: z.number(),
    title: z.string().optional(),
    titleImage: ImageUrlsSchema,
    quizUserStatus: z.nativeEnum(QuizUserStatus),
    isDraft: z.boolean(),
    answeredQuestionsCount: z.number(),
    hasSeenResults: z.boolean(),
});

export const QuizzesListForUserResponseSchema = z.object({
    quizzes: z.array(QuizListItemSchema),
});

// App-specific schemas (excludes actualValues, audios, resultsProps)
export const QuestionForAppSchema = z.object({
    id: z.string(),
    number: z.number(),
    question: z.string(),
    type: z.nativeEnum(QuestionType),
    image: ImageUrlsSchema,
    answerProps: AnswerPropsSchema,
});

export const QuizForAppSchema = z.object({
    _id: z.string(),
    number: z.number(),
    isDraft: z.boolean(),
    titleImage: ImageUrlsSchema,
    questions: z.array(QuestionForAppSchema),
});

export const QuizForAppResponseSchema = z.object({
    quiz: QuizForAppSchema,
    answeredQuestionIds: z.array(z.string()),
});

// Progress Stats Schema
export const ProgressStatsSchema = z.object({
    total: z.number(),
    completed: z.number(),
    percentage: z.number(), // 0-100
});

export const ModulesProgressResponseSchema = z.object({
    quizzes: ProgressStatsSchema,
    articles: z.object({
        total: ProgressStatsSchema,
        general: ProgressStatsSchema,
        personalized: ProgressStatsSchema,
    }),
});

// Quiz Result Schemas
// Represents the user's answer to a question
const QuestionAnswerValueSchema = z.object({
    value: z.union([z.boolean(), z.number(), z.string()]),
});

// Represents one question's result
export const QuestionResultSchema = z.object({
    questionId: z.string(),
    questionNumber: z.number(),
    questionText: z.string(),
    questionType: z.nativeEnum(QuestionType),
    userAnswer: QuestionAnswerValueSchema,
    actualValues: ActualValuesSchema.optional(),
    audioType: z.nativeEnum(AudioType).optional(),
    audio: AudioInfoSchema.optional(),
    pairWithResultNumber: z.number().optional(),
});

// Represents the complete quiz results
export const QuizResultsSchema = z.object({
    quizId: z.string(),
    quizNumber: z.number(),
    completedAt: z.string(),
    questionResults: z.array(QuestionResultSchema),
    bucket: z.nativeEnum(Bucket).optional(),
    video: VideoInfoSchema.optional(),
    articles: z
        .object({
            general: z.array(ArticleSummarySchema),
            personalized: z.array(ArticleSummarySchema),
        })
        .optional(),
});
